import { useEffect } from "react";
import { Link } from "react-router-dom";
import { articles } from "@/mocks/articles";
import { ArticleJsonLd } from "@/components/base/SEOJsonLd";

interface ArticleLayoutProps {
  slug: string;
}

export default function ArticleLayout({ slug }: ArticleLayoutProps) {
  const article = articles[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">Article Not Found</h1>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors">
            <i className="ri-arrow-left-line"></i> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const articlePath = article.slug === "pandabuy-sheet-ultimate-guide"
    ? `/${article.slug}`
    : `/article/${article.slug}`;

  return (
    <>
      <ArticleJsonLd
        pageUrl={articlePath}
        title={article.title}
        description={article.description}
        imageUrl={article.image}
      />
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="w-full mx-auto flex items-center justify-between h-16 px-4 md:px-6">
          <Link to="/" className="text-lg md:text-xl font-heading font-bold text-foreground-950">
            PandaBuy<span className="font-normal italic">.Courses</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Hub
          </Link>
        </div>
      </header>

      <main className="pt-20 pb-16 bg-background-50">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-1.5 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-600 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <span className="text-foreground-700">{article.title}</span>
              </li>
            </ol>
          </nav>

          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight text-balance">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-5 text-sm text-foreground-500">
              <span>Updated June 2026</span>
              <span aria-hidden="true">&middot;</span>
              <span>{article.readTime}</span>
              <span aria-hidden="true">&middot;</span>
              <span>Curated Shopping</span>
            </div>
          </header>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <div className="flex-1 space-y-8 text-foreground-700 leading-relaxed">
              {article.sections.map((section, idx) => (
                <section key={idx}>
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p key={pIdx} className="text-sm md:text-base" style={pIdx > 0 ? { marginTop: "1rem" } : undefined}>
                      {para}
                    </p>
                  ))}
                </section>
              ))}

              <div className="mt-10 pt-8 border-t border-background-200">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to PandaBuy Spreadsheet Hub
                </Link>
              </div>
            </div>

            <aside className="w-full lg:w-72 shrink-0">
              <div className="sticky top-24 space-y-6">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full aspect-[3/4] object-cover rounded-2xl"
                    loading="lazy"
                  />
                </div>

                <div className="bg-background-100 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-3">Quick Links</h3>
                  <ul className="space-y-2">
                    <li>
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-external-link-line text-xs"></i>
                        pandabuy sheet
                      </a>
                    </li>
                    <li>
                      <Link
                        to="/"
                        className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-home-line text-xs"></i>
                        Home Hub
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="bg-accent-50 rounded-2xl p-5">
                  <p className="text-sm font-semibold text-accent-800 mb-1">Pro Tip</p>
                  <p className="text-xs text-accent-700 leading-relaxed">
                    The best pandabuyspreadsheet resources are updated daily. Check back often — fresh finds get added all the time.
                  </p>
                </div>

                <div className="bg-background-100 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-3">More Guides</h3>
                  <ul className="space-y-2">
                    {Object.values(articles)
                      .filter((a) => a.slug !== slug)
                      .slice(0, 5)
                      .map((a) => {
                        const articlePath = a.slug === "pandabuy-sheet-ultimate-guide"
                          ? `/${a.slug}`
                          : `/article/${a.slug}`;
                        return (
                          <li key={a.slug}>
                            <Link
                              to={articlePath}
                              className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                            >
                              <i className="ri-article-line text-xs"></i>
                              <span className="line-clamp-1">{a.title}</span>
                            </Link>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
    </>
  );
}